import { FC, useState, MouseEvent } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import MainLayout from 'src/pages/private';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { useFormik } from 'formik';
import { rawMaterialInitialValues } from 'src/utils/initialValues.utils';
import { rawMaterialValidationSchema } from 'src/utils/validationScheme.utils';
import {
  apiUrls,
  dialogTitles,
  dialogSubtitles,
} from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { fetchRawMaterialUnits } from 'src/services/enums.service';
import SearchBar from 'src/components/InputFields/Searchbar';
import RawMaterialGrid from 'src/components/Grids/RawMaterialGrid';
import CustomDialog from 'src/components/DialogBox/FormBox/CustomBox';
import TextField from 'src/components/InputFields/TextField';
import UnitField from 'src/components/InputFields/UnitPicker';
import FilterPopover from 'src/components/StyledComponents/FilterPopover';
import FormikSelectField, {
  Option,
} from 'src/components/InputFields/FormikSelectField';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import {
  PrimaryActionButton,
  ButtonIcon,
  OverflowContainer,
  FilterButton,
  FilterIcon,
  FilterPopoverIcon,
  FilterPopoverTitle,
  FilterPopoverDivider,
  FilterCloseIcon,
  SmallActionButton,
} from 'src/components/StyledComponents/StyledPageComponents';
import { getBasicRawMaterialCategories } from 'src/services/rawMaterialCategory.service';
import { BasicRawMaterialCategory } from 'src/models/Management.interface';
import CategoryPicker from 'src/components/InputFields/CategoryPicker';

interface RawMaterialFilterValues {
  categoryId: string;
}

const RawMaterialManagement: FC = () => {
  const theme = useTheme();
  const smScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [materialAdded, setMaterialAdded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [unitList, setUnitList] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterValues, setFilterValues] = useState<RawMaterialFilterValues>({
    categoryId: '',
  });
  const [categoryList, setCategoryList] = useState<BasicRawMaterialCategory[]>(
    []
  );

  const { showSnackbar } = StyledSnackbar();

  const formik = useFormik({
    initialValues: rawMaterialInitialValues,
    validationSchema: rawMaterialValidationSchema,
    onSubmit: (values) => handleSubmit(values),
  });

  const filterFormik = useFormik({
    initialValues: {
      categoryPicker: '',
    },
    onSubmit: (values) => {
      setFilterValues({
        categoryId: values.categoryPicker,
      });
      setFilterOpen(false);
    },
  });

  const categoryOptions: Option[] = categoryList.map((cat) => ({
    label: cat.name,
    value: cat.id,
  }));

  const fetchUnits = async () => {
    try {
      const response = await fetchRawMaterialUnits();
      setUnitList(response);
    } catch (e) {
      const errorMessage = await getApiErrorMessage(e);
      showSnackbar(errorMessage, 'error');
    }
  };

  const fetchCategories = async () => {
    try {
      const categoryData = await getBasicRawMaterialCategories();
      setCategoryList(categoryData || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      setCategoryList([]);
    }
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const addMaterial = await axiosTokenInstance.post(
        apiUrls.ADD_RAW_MATERIAL,
        {
          name: values.name,
          code: values.code,
          availableQty: values.availableQty,
          minLevelQty: values.minLevelQty,
          unit: values.unit,
          unitPrice: values.unitPrice,
          rawMaterialCategoryId: values.rawMaterialCategoryId || null,
        }
      );
      showSnackbar(addMaterial.data.message, 'success');
      setMaterialAdded(!materialAdded);
      handleClose();
    } catch (e) {
      const errorMessage = await getApiErrorMessage(e);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (newQuery: string) => {
    setSearchTerm(newQuery);
  };

  const handleClickOpen = async () => {
    fetchUnits();
    await fetchCategories();
    setOpen(true);
  };

  const handleClose = () => {
    formik.resetForm();
    setOpen(false);
  };

  const handleFilterClick = async (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setFilterOpen(true);
    await fetchCategories();
  };

  const handleFilterClose = () => {
    filterFormik.resetForm();
    setAnchorEl(null);
    setFilterOpen(false);
  };

  const handleFilterClear = () => {
    filterFormik.resetForm();
    setFilterValues({
      categoryId: '',
    });
  };

  return (
    <MainLayout
      title="Raw Material Management"
      searchBar={
        <SearchBar
          id="search"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search raw materials by name or code"
          width="380px"
        />
      }
      button={
        <PrimaryActionButton
          className="btn-primary"
          width="200px"
          onClick={handleClickOpen}
        >
          <ButtonIcon component="i" className="bx bx-plus 1" />
          New Raw Material
        </PrimaryActionButton>
      }
      filterButton={
        <FilterButton
          className="btn-filter"
          width="100px"
          onClick={handleFilterClick}
        >
          <FilterIcon component="i" className="bx bx-filter" />
          Add Filter
        </FilterButton>
      }
    >
      <FilterPopover
        open={filterOpen}
        anchorEl={anchorEl}
        onClose={handleFilterClose}
      >
        <Box>
          <Box display="flex" alignItems="center">
            <FilterPopoverIcon component="i" className="bx bx-filter" />
            <FilterPopoverTitle>Filter Data By</FilterPopoverTitle>
          </Box>
          <FilterPopoverDivider />
          <FilterCloseIcon
            component="i"
            className="bx bx-x-circle"
            onClick={handleFilterClose}
          />
        </Box>

        <Box display="flex" justifyContent="center" mb={1}>
          <FormikSelectField
            id="categoryPicker"
            label="Category"
            height="28px"
            width="310px"
            customMessage="Category"
            textColor="var(--subtext)"
            borderColor="var(--hover-white)"
            fontSize="10px"
            fontWeight="400"
            showLabel={false}
            formikProps={filterFormik}
            options={categoryOptions}
          />
        </Box>

        <Box mt={4} display="flex" justifyContent="space-between">
          <SmallActionButton
            btnVariant="inactive"
            width="70px"
            mr={2}
            onClick={handleFilterClear}
          >
            Clear
          </SmallActionButton>

          <SmallActionButton
            className="btn-primary"
            width="70px"
            onClick={() => filterFormik.handleSubmit()}
          >
            Apply
          </SmallActionButton>
        </Box>
      </FilterPopover>

      <RawMaterialGrid
        state={materialAdded}
        searchTerm={searchTerm}
        categoryFilter={filterValues.categoryId || null}
      />
      <Box display="flex">
        <OverflowContainer display="flex" flexDirection="column">
          <CustomDialog
            height={`600px`}
            open={open}
            handleClose={handleClose}
            title={dialogTitles.ADD_RAW_MATERIAL}
            subtitle={dialogSubtitles.ADD_RAW_MATERIAL}
            onSubmit={formik.handleSubmit}
            smScreen={smScreen}
            buttonDisabled={loading}
          >
            <Box display="flex" flexDirection="column">
              <Box display="flex" justifyContent="space-between" gap={2} mt={2}>
                <TextField
                  width="300px"
                  id="name"
                  label="Name"
                  formikProps={formik}
                />

                <TextField
                  width="300px"
                  id="code"
                  label="Code"
                  formikProps={formik}
                />
              </Box>

              <Box display="flex" justifyContent="space-between" gap={2}>
                <UnitField
                  width="300px"
                  id="unit"
                  label="Unit"
                  formikProps={formik}
                  unitList={unitList}
                />

                <TextField
                  width="300px"
                  id="unitPrice"
                  placeholder="00.00"
                  label="Unit Price"
                  formikProps={formik}
                />
              </Box>

              <Box display="flex" justifyContent="space-between" gap={2}>
                <TextField
                  width="300px"
                  id="minLevelQty"
                  label="Minimum Quantity Level"
                  formikProps={formik}
                />

                <CategoryPicker
                  id="rawMaterialCategoryId"
                  label="Category"
                  categories={categoryList}
                  value={formik.values.rawMaterialCategoryId}
                  onChange={(value) =>
                    formik.setFieldValue('rawMaterialCategoryId', value)
                  }
                  width="300px"
                  allowNone={true}
                  noneLabel="None"
                />
              </Box>
            </Box>
          </CustomDialog>
        </OverflowContainer>
      </Box>
    </MainLayout>
  );
};

export default RawMaterialManagement;
